import React, { useEffect } from 'react'
import clsx from 'clsx'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import Layout from '../Layout'
import BlurText from '../ui/BlurText'

import LivingTwinCanvas from './LivingTwinCanvas'

// Sopra l'immagine del pannello sinistro gira LivingTwinCanvas (respiro e
// battito del gemello), condiviso con LeaRes: il pannello e' `relative` per
// contenere il canvas, e il claim e' `relative` per restargli sopra.
//
// «powered by Medlea» porta al sito dell'azienda, in una scheda nuova per non
// far perdere la pagina di login.
const MEDLEA_SITE = 'https://medlea-tech.com'

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

/**
 * Bring the section under the fold into view and give it the focus, so a keyboard user
 * lands where the eyes go. Smooth only when the user did not ask for reduced motion.
 */
export const revealBelow = (id, { smooth = true } = {}) => {
  const target = typeof document !== 'undefined' ? document.getElementById(id) : null
  if (!target) return false
  target.scrollIntoView?.({
    behavior: smooth && !prefersReducedMotion() ? 'smooth' : 'auto',
    block: 'start',
  })
  target.focus?.({ preventScroll: true })
  return true
}

// The arrow under the login form: an anchor (it works without JavaScript and with the
// keyboard), enhanced with a smooth scroll that keeps the hash for sharing.
const BelowHint = ({ id, label }) => (
  <a
    href={`#${id}`}
    data-testid="unauthorized-below-hint"
    onClick={(e) => {
      if (revealBelow(id)) {
        e.preventDefault()
        window.history.replaceState(window.history.state, '', `#${id}`)
      }
    }}
    className="group flex-shrink-0 mt-2 mb-1 flex flex-col items-center gap-1 rounded-lg px-4 py-2 text-xs taller:text-sm tracking-[0.2em] uppercase text-white text-opacity-70 hover:text-opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-bright-blue transition"
  >
    <span>{label}</span>
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="h-5 w-5 motion-safe:animate-bounce group-hover:text-bright-blue"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M6 9l6 6 6-6" />
    </svg>
  </a>
)

const UnauthorizedLayout = ({
  children,
  title,
  subtitle,
  question,
  text,
  hancleClick,
  showPoweredBy = false,
  // Content under the fold (the login page's Info & costs): the page scrolls, and an
  // arrow under the right column leads there. `revealOnMount` opens on it (/costs).
  below = null,
  belowId = 'below',
  belowHintLabel = '',
  revealOnMount = false,
}) => {
  const { t } = useTranslation('auth')
  const navigate = useNavigate()
  const goLogin = () => navigate('/login')

  useEffect(() => {
    if (!below) return
    const wanted = revealOnMount || window.location.hash === `#${belowId}`
    if (wanted) revealBelow(belowId, { smooth: false })
    // Only on mount: scrolling back up must not be undone by a re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div
      data-testid="unauthorized-scroll"
      className={clsx('w-screen h-screen bg-dark-blue', below && 'overflow-y-auto overflow-x-hidden')}
    >
      <Layout>
        <main className="h-[100svh] m-[-12px] desktop:m-[-20px] flex justify-between items-center bg-landing bg-no-repeat bg-cover bg-[right_top] tablet:bg-none">
          <div className="relative h-full w-[68%] p-10 bg-landing bg-no-repeat bg-cover bg-[right_top] hidden tablet:flex items-start justify-start">
            <LivingTwinCanvas />
            <div className="relative mt-[200px] tracking-[1.5px]">
              <h1 className="tablet:mb-4 tablet:text-5xl laptop:text-6xl desktop:text-7xl tablet:leading-tight laptop:leading-[67px] text-left font-bold">
                <BlurText
                  text={t('tagline.segment')}
                  animateBy="words"
                  direction="top"
                  startDelay={0}
                />
                <br />
                <BlurText
                  text={t('tagline.reconstruct')}
                  animateBy="words"
                  direction="top"
                  startDelay={80}
                />
                <br />
                <BlurText
                  text={t('tagline.simulate')}
                  animateBy="words"
                  direction="top"
                  startDelay={160}
                />
              </h1>
              <p className="text-sm tablet:text-lg laptop:text-[22px] text-left font-light">
                <BlurText
                  text={t('tagline.motto')}
                  animateBy="words"
                  direction="top"
                  startDelay={320}
                />
              </p>
            </div>
          </div>
          <div className="relative pr-[20px] pl-[20px] tablet:pl-0 w-full tablet:w-[calc(32%-20px)] h-[95%] taller:h-[90%] flex flex-col items-center bg-[#0F1A33]/20 tablet:bg-transparent">
            {/* Header: logo area (non-absolute to avoid overlap) */}
            <div className="w-full flex flex-col items-center pt-6 mb-2">
              <img
                className="w-[120px] taller:w-[140px] tallest:w-[168px] max-w-full h-auto mb-2 cursor-pointer hover:opacity-90 transition"
                src="/assets/images/flowleap-logo.svg"
                alt="LeaRes Logo"
                onClick={goLogin}
              />
              {showPoweredBy && (
                <a
                  href={MEDLEA_SITE}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-row items-start gap-1 hover:opacity-90 transition"
                >
                  <p className="text-white text-opacity-70 text-[11px] taller:text-xs font-light mt-[14px]">
                    powered by
                  </p>
                  <img
                    className="w-[46px] taller:w-[57px] tallest:w-[66px]"
                    src="/assets/images/medlea-logo.svg"
                    alt="Medlea Logo"
                  />
                </a>
              )}
            </div>
            {/* Content */}
            <div className="flex-1 w-full flex flex-col justify-start items-center text-white min-h-0 overflow-y-auto">
              {title && (
                <h2 className="text-center text-xl taller:text-2xl font-semibold mt-2 mb-1">
                  {title}
                </h2>
              )}
              {/* Subtitle intentionally removed for a cleaner signup layout */}
              {children}
            </div>
            {question && text && (
              <div className="flex-shrink-0 pt-4 pb-2 flex justify-center items-center gap-1.5 tracking-wide text-sm taller:text-base">
                <p className="font-light text-white text-opacity-80">
                  {question}
                </p>
                <button
                  className="text-white hover:text-opacity-100 text-opacity-90 transition hover:scale-105"
                  onClick={hancleClick}
                >
                  {text}
                </button>
              </div>
            )}
            {below && belowHintLabel && <BelowHint id={belowId} label={belowHintLabel} />}
          </div>
        </main>
      </Layout>
      {below}
    </div>
  )
}

export default UnauthorizedLayout
